import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  type AppointmentOutcome,
  getOutcomeStatusInfo,
  OUTCOME_STATUS_VALUES,
} from "@/domains/appointment/appointments/shared/utils/appointment-status-style";

interface AppointmentOutcomeDropdownProps {
  appointmentId: string;
  currentOutcome?: string | null;
  onOutcomeChange?: (outcome: AppointmentOutcome | null) => void;
}

export function AppointmentOutcomeDropdown({
  appointmentId: _appointmentId,
  currentOutcome,
  onOutcomeChange,
}: AppointmentOutcomeDropdownProps) {
  const { t } = useTranslation("newCalendar");
  const [isUpdating, setIsUpdating] = useState(false);

  const handleOutcomeChange = async (newOutcome: AppointmentOutcome | null) => {
    if (newOutcome === currentOutcome) return;

    setIsUpdating(true);
    try {
      // Call the parent component's outcome change handler
      // This will trigger the GraphQL mutation in the appointment list view
      await onOutcomeChange?.(newOutcome);
    } catch (error) {
      console.error("Failed to update appointment outcome:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const outcomeInfo = getOutcomeStatusInfo(currentOutcome as AppointmentOutcome);
  const OutcomeIconComponent = outcomeInfo.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-6 px-2 gap-1 border ${outcomeInfo.backgroundColor} ${outcomeInfo.borderColor} hover:opacity-80`}
          onClick={(e) => e.stopPropagation()}
        >
          {isUpdating ? (
            <>
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
              <span className="text-xs font-medium">{t("common.updating")}</span>
            </>
          ) : (
            <>
              <OutcomeIconComponent className={`w-3 h-3 ${outcomeInfo.iconColor}`} />
              <span className={`text-xs font-medium ${outcomeInfo.textColor}`}>
                {outcomeInfo.text}
              </span>
              <ChevronDown className="w-3 h-3" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {OUTCOME_STATUS_VALUES.map((outcome) => {
          const optionOutcomeInfo = getOutcomeStatusInfo(outcome);
          const OptionIconComponent = optionOutcomeInfo.icon;
          return (
            <DropdownMenuItem
              key={outcome}
              onClick={(e) => {
                e.stopPropagation();
                handleOutcomeChange(outcome);
              }}
              disabled={currentOutcome === outcome}
            >
              <OptionIconComponent className={`w-3 h-3 ${optionOutcomeInfo.iconColor} mr-2`} />
              {optionOutcomeInfo.text}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
